/**
 * Google Tag Manager plugin
 *
 * - GTM script loads via useScriptGoogleTagManager() in app.vue (@nuxt/scripts)
 * - dataLayer is initialized in app.vue via useHead() before GTM loads
 * - Sends `nuxt_route` events (GTM trigger CE - nuxt_route → GA4 Virtual Page View)
 * - Preserves GTM Preview query params during SPA navigation
 */

export default defineNuxtPlugin(nuxtApp => {
    const router = useRouter();
    const route = useRoute();
    const previewQueryKeys = ['gtm_auth', 'gtm_preview', 'gtm_debug'] as const;
    const isPreviewSession = previewQueryKeys.some(key => router.currentRoute.value.query[key]);

    router.beforeEach((to, from) => {
        if (!isPreviewSession) {
            return;
        }

        const missingPreviewParams = previewQueryKeys.some(key => from.query[key] && !to.query[key]);

        if (!missingPreviewParams) {
            return;
        }

        return {
            path: to.path,
            hash: to.hash,
            query: {
                ...to.query,
                ...Object.fromEntries(previewQueryKeys.filter(key => from.query[key]).map(key => [key, from.query[key]]))
            }
        };
    });

    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
    }

    let lastPageViewPath: string | null = null;
    let lastPageViewTime = 0;
    const PAGEVIEW_DEBOUNCE_MS = 500;
    let hasTrackedInitialLoad = false;

    const trackPageView = () => {
        if (typeof window === 'undefined' || !window.dataLayer) {
            return;
        }

        const currentPath = route.path;
        const currentTime = Date.now();

        if (lastPageViewPath === currentPath && lastPageViewTime && currentTime - lastPageViewTime < PAGEVIEW_DEBOUNCE_MS) {
            return;
        }

        lastPageViewPath = currentPath;
        lastPageViewTime = currentTime;

        trackVirtualPageView(route.fullPath, document.title);
    };

    nuxtApp.hook('app:mounted', () => {
        setTimeout(() => {
            trackPageView();
            hasTrackedInitialLoad = true;
        }, 100);
    });

    nuxtApp.hook('page:finish', () => {
        if (hasTrackedInitialLoad) {
            trackPageView();
        }
    });

    return {
        provide: {
            gtm: {
                push(event: Record<string, unknown>) {
                    if (typeof window !== 'undefined' && window.dataLayer) {
                        window.dataLayer.push(event);
                    }
                }
            }
        }
    };
});
